import { useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { Col, Row, Table } from "react-bootstrap";
import TopNavbar from "../topNavbar/TopNavbar";
import Sidebar from "../sidebar/Sidebar";
import { Footer } from "../../Footer";
import { SecondaryMarketAnnouncement } from "../../redux/auth-reducer/auth-service";
import { usePagedList } from "../../hooks/usePagedList";
import { ResultsPager } from "./ResultsPager";
import { formatResultDate } from "./formatResultDate";

/**
 * Every column of a resale result, in order. One list drives both the table and the expanded
 * per-auction breakdown, so the two cannot drift apart again (the Super-admin copy showed Level
 * in one and not the other).
 *
 * "Buyer" and "Winning Bid Amount" are what usp_GetAllSMAnnouncement returns: the holding it joins
 * is the one settlement creates for the listing's top bidder, and BidAmount is MAX(BidAmount). The
 * admin copies called them "Team Owner Name" and "Bid Amount", which on a resale reads as the
 * seller. The mobile app uses the same labels as here. Seller (#143) is the player whose holding
 * the listing was cut from.
 */
const FIELDS = [
  { name: "Auction Name", value: (row) => row?.auctionName },
  { name: "Team Name", value: (row) => row?.teamName },
  { name: "Buyer", value: (row) => row?.teamOwnerName },
  { name: "Seller", value: (row) => row?.sellerName },
  {
    name: "Start Date & Time",
    value: (row) => row?.startDate,
    format: (row) => formatResultDate(row?.startDate),
  },
  {
    name: "End Date & Time",
    value: (row) => row?.endDate,
    format: (row) => formatResultDate(row?.endDate),
  },
  { name: "Winning Bid Amount", value: (row) => row?.bidAmount },
  { name: "Level", value: (row) => row?.level, adminOnly: true },
  { name: "Ownership %", value: (row) => row?.ownerShipPercentage },
];

const display = (field, row) => (field.format ? field.format(row) : field.value(row));

// One row per listing lot and holding; legacy listings can carry more than one holding.
const listingKey = (row) => `${row?.auctionId}-${row?.teamId}-${row?.level}-${row?.teamOwnerId}`;

/** Every listing of the expanded row's auction on the current page, the row itself included. */
const AuctionBreakdown = ({ data, rows, fields }) => (
  <div>
    <h4 className="mb-3">Details for {data.auctionName}</h4>
    <Table striped bordered hover size="sm" className="text-center">
      <thead>
        <tr>
          {fields.map((field) => (
            <th key={field.name}>{field.name}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows
          .filter((row) => row.auctionId === data.auctionId)
          .map((row) => (
            <tr key={listingKey(row)}>
              {fields.map((field) => (
                <td key={field.name}>{display(field, row)}</td>
              ))}
            </tr>
          ))}
      </tbody>
    </Table>
  </div>
);

/**
 * Secondary Market Results for every role: mounted from /WebAdmin, /SuperAdmin and /Player. Which
 * resales a role sees is decided by the server (usp_GetAllSMAnnouncement scopes rows by the
 * caller), not by anything here.
 *
 * @param {{showLevel?: boolean}} props showLevel adds the listing's Level column (admin roles).
 */
export const SecondaryMarketResults = ({ showLevel = false }) => {
  const [data, setData] = useState();
  const [searchText, setSearchText] = useState("");
  const pager = usePagedList(SecondaryMarketAnnouncement);

  const fields = useMemo(
    () => (showLevel ? FIELDS : FIELDS.filter((field) => !field.adminOnly)),
    [showLevel]
  );
  const columns = useMemo(
    () =>
      fields.map((field) => ({
        name: <strong>{field.name}</strong>,
        selector: field.value,
        format: field.format,
        sortable: true,
      })),
    [fields]
  );

  function sidebarfun() {
    setData(!data);
  }

  // The table's row identity (keyField below). Without one it keys rows by index, so a search or a
  // page change left an open row's expansion on whatever listing landed at the same position.
  const keyedRows = useMemo(
    () => pager.rows.map((row) => ({ ...row, listingKey: listingKey(row) })),
    [pager.rows]
  );

  // Searches the loaded page; auction name is the only searchable field these rows carry.
  const needle = searchText.toLowerCase();
  const filteredRows = needle
    ? keyedRows.filter((row) => row?.auctionName?.toLowerCase().includes(needle))
    : keyedRows;

  return (
    <>
      <TopNavbar sidebarfun={sidebarfun} />
      <div className="dasboard-group">
        <Sidebar data={data} />
        <div
          className={
            data ? "dashboard-content full-contnet" : "dashboard-content"
          }
        >
          <Row>
            <Col md={6}>
              <h1 className="heading-dash">Secondary Market Results</h1>
            </Col>
          </Row>

          <div className="group_data_table sec-announcement">
            <div className="search-input results-search d-flex align-items-center">
              <input
                type="text"
                className="form-control mb-3 mt-3"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button className="btn btn-clear" onClick={() => setSearchText("")}>
                  <span>&times;</span> {/* This represents the close icon */}
                </button>
              )}
            </div>

            <div>
              <DataTable
                columns={columns}
                data={filteredRows}
                keyField="listingKey"
                expandableRows
                expandableRowsComponent={AuctionBreakdown}
                expandableRowsComponentProps={{ rows: pager.rows, fields }}
              />
              <ResultsPager
                page={pager.page}
                totalPages={pager.totalPages}
                perPage={pager.perPage}
                pageSizes={pager.pageSizes}
                onPerPageChange={pager.changePerPage}
                onPrevious={pager.previousPage}
                onNext={pager.nextPage}
              />
            </div>
          </div>
        </div>
        <div className={data ? "footer" : "footer-content"}>
          <Footer />
        </div>
      </div>
    </>
  );
};
